/**
 * Props du bloc CatalogueDecoupages (liste curatée + liens).
 * KEEP IN SYNC avec inventozen-frontpublic/.../catalogue-decoupages.ts
 */

import {
  normalizeButtonLink,
  type ButtonLink,
} from "@/lib/cms-puck/blocks/button";

export type CatalogueDecoupagesTarget = "ARTWORKS" | "MULTIPRINTS";

/** Ligne locale au bloc : section CR + lien (listing classique par défaut). */
export type CatalogueDecoupageListItem = {
  id: string;
  sectionId: string;
  link: ButtonLink;
};

/** Lien par défaut : URL classique du découpage (`/{slug-découpage}`). */
export function defaultDecoupageItemLink(
  sectionId: string,
  target: CatalogueDecoupagesTarget = "ARTWORKS",
): ButtonLink {
  return {
    targetType: "CATALOG_DECOUPAGE",
    pageId: null,
    url: null,
    systemKey: null,
    catalogDecoupage: { target, decoupageId: sectionId },
    openInNew: false,
  };
}

/** @deprecated Prefer `defaultDecoupageItemLink(sectionId, target)`. */
export const DEFAULT_DECOUPAGE_ITEM_LINK: ButtonLink =
  defaultDecoupageItemLink("");

export function newCatalogueDecoupageListItemId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `dec-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createCatalogueDecoupageListItem(
  sectionId: string,
  opts?: {
    link?: ButtonLink;
    target?: CatalogueDecoupagesTarget;
  },
): CatalogueDecoupageListItem {
  const target = opts?.target ?? "ARTWORKS";
  return {
    id: newCatalogueDecoupageListItemId(),
    sectionId,
    link: normalizeItemLink(
      opts?.link ?? defaultDecoupageItemLink(sectionId, target),
      sectionId,
      target,
    ),
  };
}

function normalizeItemLink(
  raw: ButtonLink,
  sectionId: string,
  blockTarget: CatalogueDecoupagesTarget,
): ButtonLink {
  const link = normalizeButtonLink(raw);
  // Respecter le choix utilisateur (PAGE même sans pageId encore choisi).
  if (link.targetType === "CUSTOM") {
    return { ...link, targetType: "CUSTOM", catalogDecoupage: null };
  }
  if (link.targetType === "PAGE") {
    return {
      ...link,
      targetType: "PAGE",
      catalogDecoupage: null,
      systemKey: null,
    };
  }
  if (link.targetType === "CATALOG_DECOUPAGE") {
    return {
      ...link,
      targetType: "CATALOG_DECOUPAGE",
      pageId: null,
      url: null,
      systemKey: null,
      catalogDecoupage: {
        target: link.catalogDecoupage?.target ?? blockTarget,
        decoupageId: link.catalogDecoupage?.decoupageId || sectionId,
      },
    };
  }
  // Legacy / inconnu → listing classique.
  return defaultDecoupageItemLink(sectionId, blockTarget);
}

export function normalizeCatalogueDecoupageListItems(
  value: unknown,
  blockTarget: CatalogueDecoupagesTarget = "ARTWORKS",
): CatalogueDecoupageListItem[] {
  if (!Array.isArray(value)) return [];
  const out: CatalogueDecoupageListItem[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Partial<CatalogueDecoupageListItem>;
    const sectionId =
      typeof o.sectionId === "string" ? o.sectionId.trim() : "";
    if (!sectionId) continue;
    const id =
      typeof o.id === "string" && o.id.trim()
        ? o.id.trim()
        : newCatalogueDecoupageListItemId();
    out.push({
      id,
      sectionId,
      link: normalizeItemLink(
        (o.link as ButtonLink) ?? defaultDecoupageItemLink(sectionId, blockTarget),
        sectionId,
        blockTarget,
      ),
    });
  }
  return out;
}
